import { ReactNode, createContext, useMemo } from 'react';
import useLoanTypes from '~/hooks/useLoanTypes';
import useLocalStorage from '~/hooks/useLocalStorage';

export type LoanOfficerSearchParams = {
  fullname: string;
  nmls: string;
  license_area: string[];
  transaction_address: {
    state: StateAlias | EmptyString;
    zip: string;
    city: string;
    county: string;
  };
  office: {
    position?: string;
    branch_manager: boolean;
    blank_position: boolean;
    name?: string;
    license?: string;
    nmls?: string;
    type?: string[];
    strict?: boolean;
    address: {
      state: StateAlias | EmptyString;
      zip: string;
      city: string;
      county: string;
    };
  };
  period: Period;
  financial_data: {
    loan_and_transaction: {
      loan_volume: ParamInterval;
      transaction_volume: ParamInterval;
    };
    loan_type: {
      fha?: ParamInterval;
      va?: ParamInterval;
      pp?: ParamInterval;
      other?: ParamInterval;
      sba?: ParamInterval;
      cnv?: ParamInterval;
      rev?: ParamInterval;
    };
    transaction_type: {
      purchase: ParamInterval;
      refinance: ParamInterval;
      construction: ParamInterval;
      heloc: ParamInterval;
    };
  };
};

export const defaultFromToRange = { from: '', to: '' };

export const defaultAmountQuantityRange = {
  amount: defaultFromToRange,
  quantity: defaultFromToRange,
};

export type LoanOfficersSearchParamsContextState = {
  searchParams: LoanOfficerSearchParams;
  defaultValues: LoanOfficerSearchParams;
  setSearchParams: (newParams: LoanOfficerSearchParams) => void;
};

const initialState: LoanOfficersSearchParamsContextState = {
  defaultValues: {} as LoanOfficerSearchParams,
  searchParams: {} as LoanOfficerSearchParams,
  setSearchParams: () => {},
};

type LoanOfficersSearchParamsProviderProps = {
  children: ReactNode;
};

const LoanOfficersSearchParamsContext = createContext(initialState);

function LoanOfficersSearchParamsProvider({ children }: LoanOfficersSearchParamsProviderProps) {
  const { loanTypes } = useLoanTypes();

  const defaultValues: LoanOfficerSearchParams = useMemo(
    () => ({
      fullname: '',
      nmls: '',
      license_area: [],
      transaction_address: {
        state: '',
        city: '',
        zip: '',
        county: '',
      },
      office: {
        name: '',
        position: '',
        branch_manager: false,
        blank_position: false,
        license: '',
        type: [],
        strict: false,
        address: {
          state: '',
          city: '',
          zip: '',
          county: '',
        },
      },
      period: 14,
      financial_data: {
        loan_and_transaction: {
          loan_volume: defaultAmountQuantityRange,
          transaction_volume: defaultAmountQuantityRange,
        },
        loan_type: Object.fromEntries(loanTypes.map((type) => [type, defaultAmountQuantityRange])),
        transaction_type: {
          purchase: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          refinance: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          construction: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          heloc: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
        },
      },
    }),
    [loanTypes]
  );

  const [searchParams, setSearchParams] = useLocalStorage<LoanOfficerSearchParams>(
    'searchParams:loanOfficers',
    defaultValues
  );

  return (
    <LoanOfficersSearchParamsContext.Provider
      value={{ searchParams, setSearchParams, defaultValues }}
    >
      {children}
    </LoanOfficersSearchParamsContext.Provider>
  );
}

export { LoanOfficersSearchParamsProvider, LoanOfficersSearchParamsContext };
